// THE BLUE SCREEN — the crash cabinet.
//
// The multiplier is system uptime and the crash is a BSOD. The reveal is a replay: the outcome
// is fixed on chain before the animation starts, and this component renders the settled uptime
// rather than inventing one of its own.
//
// Dressed as a beige CRT on a felt table: the uptime is an LED readout on the tube, the jump
// line is an XP trackbar, and the crash is the real blue screen.
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { CHIP, revealBlueScreen } from 'lib/casino';
import { useBet, useStakeCap } from './state';
import { FONT, GroupBox, HouseRules, Led, Outcome, Panel, PlayButton, StakeBox, T, fmtChip, parseUnits, xpButton } from './ui';

const MIN_LINE = 10100;
const MAX_LINE = 160000;
const RTP_BPS = 9000;

/** The same arithmetic the contract prints: chance = 0.90 / m. */
const chanceOf = lineBps => RTP_BPS / lineBps;

export default function BlueScreen({ casino }) {
  const [amount, setAmount] = useState('5');
  const [line, setLine] = useState(20000);
  const bet = useBet('blueScreen', casino);
  const [uptime, setUptime] = useState(null);

  // The altitude the flight actually reached, read back from the contract's own pure view. The
  // BSOD shows the chain's number, never one this component made up.
  useEffect(() => {
    let alive = true;
    setUptime(null);
    if (!bet.result) return undefined;
    revealBlueScreen(bet.result.id, bet.result.randomness)
      .then(v => {
        if (alive) setUptime(v);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [bet.result]);

  const chance = chanceOf(line);
  const allIn = useMemo(() => parseUnits(amount, CHIP.decimals), [amount]);
  const stake = (allIn * BigInt(10000)) / BigInt(10500);
  const payout = (stake * BigInt(line)) / BigInt(10000);

  // The house refuses a bet whose worst case it cannot cover, and the refusal is a revert with
  // no explanation. Read the live ceiling and never let the player submit past it.
  const stakeCap = useStakeCap('blueScreen', line, casino, amount, setAmount);
  const overCap = stakeCap !== null && stakeCap > BigInt(0) && allIn > stakeCap;
  const canPlay = casino.ready && allIn > BigInt(0) && !overCap;
  const busy = bet.phase === 'waiting' || bet.phase === 'settling' || bet.phase === 'placing' || bet.phase === 'approving';
  const lineText = (line / 10000).toFixed(2);

  return (
    <Div>
      <div className="bs__monitor">
        <div className="bs__screen" data-state={bet.phase}>
          {bet.phase === 'done' && bet.result && !bet.result.won ? (
            <div className="bs__bsod">
              <div className="bs__bsod__bar">INTERNET</div>
              <p>A problem has been detected and Windows has been shut down to prevent damage.</p>
              <p>UNEXPECTED_KERNEL_MODE_TRAP</p>
              <p>
                Uptime reached{' '}
                <b>
                  {uptime === null
                    ? '…'
                    : uptime === BigInt(0)
                    ? 'nothing — it never cleared POST'
                    : `${(Number(uptime) / 10000).toFixed(2)}x`}
                </b>
                , short of your {(line / 10000).toFixed(2)}x line. Your stake is gone.
              </p>
              <p className="bs__bsod__dim">Press AGAIN to continue _</p>
            </div>
          ) : (
            <div className="bs__uptime">
              <div className="bs__uptime__label">SYSTEM UPTIME</div>
              <div className={`bs__uptime__value${busy ? ' booting' : ''}`}>
                <Led text={busy ? '--.--' : `${lineText}x`} scale={1.5} />
              </div>
              <div className="bs__uptime__sub">
                {bet.phase === 'done' && bet.result && bet.result.won
                  ? 'bailed out clean'
                  : busy
                  ? 'BOOTING…'
                  : `bail out here · holds ${(chance * 100).toFixed(2)}% of the time`}
              </div>
            </div>
          )}
        </div>
        <div className="bs__bezel">
          <span className="bs__brand">INTERNET · CRT 17&quot;</span>
          <span className={`bs__power${busy ? ' busy' : ''}`} />
        </div>
      </div>

      <Panel>
        <GroupBox legend="Jump line">
          <div className="bs__slider">
            <input
              type="range"
              min={MIN_LINE}
              max={MAX_LINE}
              step={100}
              value={line}
              disabled={bet.phase !== 'idle' && bet.phase !== 'done'}
              onChange={e => setLine(Number(e.target.value))}
            />
            <div className="bs__ticks">
              <span>1.01x</span>
              <span>4x</span>
              <span>8x</span>
              <span>16x</span>
            </div>
          </div>
          <div className="bs__quote">
            <div>
              <span className="k">Jump line</span>
              <span className="v">{lineText}x</span>
            </div>
            <div>
              <span className="k">Chance it holds</span>
              <span className="v">{(chance * 100).toFixed(2)}%</span>
            </div>
            <div>
              <span className="k">Pays</span>
              <span className="v">
                {fmtChip(payout)} {CHIP.symbol}
              </span>
            </div>
          </div>
        </GroupBox>

        <StakeBox
          value={amount}
          onChange={setAmount}
          disabled={bet.phase !== 'idle' && bet.phase !== 'done'}
        />

        <PlayButton
          label="BOOT"
          onClick={() => bet.play(allIn, [line])}
          disabled={!canPlay}
          phase={bet.phase}
          note={bet.note}
          countdown={bet.countdown}
        />

        {bet.phase === 'stuck' ? (
          <div className="bs__stuck">
            Your bet is placed and still open. <button onClick={bet.retrySettle}>Settle again</button>
          </div>
        ) : null}

        <Outcome result={bet.result} onAgain={bet.reset} />

        <HouseRules
          fair={false}
          practice={casino.practice}
        />
      </Panel>
    </Div>
  );
}

const Div = styled.div`
  .bs__monitor {
    background: linear-gradient(180deg, #e9e5d6, #cfc9b6);
    border: 2px solid #b5ae9a;
    border-radius: 10px 10px 6px 6px;
    padding: 12px 12px 6px;
    box-shadow: inset 0 1px 0 #fff, 0 4px 8px rgba(0, 0, 0, 0.5);
  }
  .bs__screen {
    background: radial-gradient(ellipse at 50% 40%, #0d1626, #05070c 75%);
    border: 3px solid #4a4638;
    border-radius: 6px;
    min-height: 150px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 12px;
    box-shadow: inset 0 0 30px rgba(0, 0, 0, 0.8);
    position: relative;
    overflow: hidden;
  }
  .bs__screen::after {
    content: '';
    position: absolute;
    inset: 0;
    background: repeating-linear-gradient(0deg, rgba(255, 255, 255, 0.035) 0 1px, transparent 1px 3px);
    pointer-events: none;
  }
  .bs__bezel {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 6px 4px 0;
  }
  .bs__brand {
    font: 9px ${FONT.web};
    letter-spacing: 2px;
    color: #6f6857;
  }
  .bs__power {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #3bd23b;
    box-shadow: 0 0 5px #3bd23b;
  }
  .bs__power.busy {
    background: #ffb300;
    box-shadow: 0 0 6px #ffb300;
    animation: cs-blink 0.6s steps(2, start) infinite;
  }
  @keyframes cs-blink {
    to {
      visibility: hidden;
    }
  }
  .bs__uptime {
    text-align: center;
  }
  .bs__uptime__label {
    font: 9px ${FONT.web};
    letter-spacing: 4px;
    color: #9fb0c8;
  }
  .bs__uptime__value {
    margin: 6px 0;
    display: flex;
    justify-content: center;
  }
  .bs__uptime__value.booting {
    animation: cs-blink 0.8s steps(2, start) infinite;
  }
  .bs__uptime__sub {
    font: 10px ${FONT.web};
    color: #c9d4e6;
  }
  .bs__bsod {
    background: #0827a8;
    color: #fff;
    font: 10px ${FONT.mono};
    line-height: 1.6;
    padding: 12px 14px;
    width: 100%;
    align-self: stretch;
    position: relative;
    z-index: 1;
  }
  .bs__bsod__bar {
    background: #c8c8c8;
    color: #0827a8;
    display: inline-block;
    padding: 0 10px;
    margin-bottom: 8px;
    letter-spacing: 3px;
  }
  .bs__bsod p {
    margin: 0 0 7px;
  }
  .bs__bsod__dim {
    color: #b9c6f2;
  }
  .bs__slider input {
    width: 100%;
    -webkit-appearance: none;
    appearance: none;
    height: 22px;
    background: transparent;
    margin: 0;
  }
  .bs__slider input::-webkit-slider-runnable-track {
    height: 4px;
    background: #fff;
    border: 1px solid #9d9da1;
    box-shadow: inset 1px 1px 0 #808080;
  }
  .bs__slider input::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 11px;
    height: 21px;
    margin-top: -9px;
    background: linear-gradient(180deg, #fff, #ecebe5 86%, #d8d0c4);
    border: 1px solid #003c74;
    border-radius: 2px 2px 6px 6px;
    cursor: pointer;
  }
  .bs__slider input::-moz-range-track {
    height: 4px;
    background: #fff;
    border: 1px solid #9d9da1;
  }
  .bs__slider input::-moz-range-thumb {
    width: 11px;
    height: 21px;
    background: linear-gradient(180deg, #fff, #ecebe5 86%, #d8d0c4);
    border: 1px solid #003c74;
    border-radius: 2px 2px 6px 6px;
  }
  .bs__slider input:disabled::-webkit-slider-thumb {
    border-color: #c0c0c0;
  }
  .bs__ticks {
    display: flex;
    justify-content: space-between;
    font: 9px ${FONT.ui};
    color: #555;
  }
  .bs__quote {
    display: flex;
    gap: 6px;
    margin-top: 8px;
    flex-wrap: wrap;
  }
  .bs__quote > div {
    flex: 1;
    min-width: 96px;
    background: #fff;
    border: 1px solid #7f9db9;
    box-shadow: inset 1px 1px 0 #d6dde5;
    padding: 3px 6px;
  }
  .bs__quote .k {
    display: block;
    font-size: 9px;
    color: #555;
  }
  .bs__quote .v {
    display: block;
    font-size: 12px;
    font-weight: 700;
    color: #000;
    margin-top: 1px;
  }
  .bs__stuck {
    margin-top: 8px;
    font: 10px ${FONT.ui};
    color: #8a0000;
  }
  .bs__stuck button {
    ${xpButton};
    min-width: 0;
    min-height: 19px;
    padding: 0 8px;
    margin-left: 6px;
  }
`;
